import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { useNavigate } from 'react-router-dom';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { toastify } from '../../../../../../utils/toast';

const CustomersTab = () => {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [rows, setRows] = useState(10);
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    const rowsPerPageOptions = [10, 20, 50, 100];

    useEffect(() => {
        if (selectedFacilityId) {
            fetchFacilityCustomers();
        }
    }, [selectedFacilityId]);

    const fetchFacilityCustomers = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/customer_management/get_facility_customer_accounts/${selectedFacilityId}`,
                'GET'
            );

            if (response && response.success) {
                const customersData = response.data.data || [];
                setCustomers(customersData);

                // After setting customers, fetch their balances
                if (customersData.length > 0) {
                    await fetchCustomersBalances(customersData);
                }
            } else {
                toastify(response?.error || 'Failed to fetch customer accounts', 'error');
                setCustomers([]);
            }
        } catch (error) {
            console.error('Error fetching customer accounts:', error);
            toastify('An error occurred while fetching customer accounts', 'error');
            setCustomers([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomersBalances = async (customersData) => {
        try {
            setBalanceLoading(true);

            // Create an array of balance fetch promises
            const balancePromises = customersData.map(customer =>
                makeRequest2(
                    `/api/app/power_meter_management/controllers/customer_management/get_customer_account_balance/${customer._id || customer.id}`,
                    'GET'
                ).catch(error => {
                    console.error(`Error fetching balance for customer ${customer.account_no}:`, error);
                    return null; // Return null for failed requests
                })
            );

            // Wait for all balance requests to complete
            const balanceResponses = await Promise.allSettled(balancePromises);

            // Update customers with their balance data
            const updatedCustomers = customersData.map((customer, index) => {
                const balanceResult = balanceResponses[index];

                if (balanceResult.status === 'fulfilled' &&
                    balanceResult.value &&
                    balanceResult.value.success) {

                    const balanceData = balanceResult.value.data.data;
                    return {
                        ...customer,
                        currentBalance: balanceData.currentBalance || 0,
                        negativeBalance: balanceData.negativeBalance || 0,
                        balanceLoaded: true
                    };
                } else {
                    // Keep original balance or set defaults if balance fetch failed
                    return {
                        ...customer,
                        currentBalance: customer.accountBalance || 0,
                        negativeBalance: 0,
                        balanceLoaded: false
                    };
                }
            });

            setCustomers(updatedCustomers);
        } catch (error) {
            console.error('Error fetching customers balances:', error);
        } finally {
            setBalanceLoading(false);
        }
    };

    const statusBodyTemplate = (rowData) => {
        const statusColors = {
            'Active': 'bg-success',
            'Inactive': 'bg-danger'
        };

        return (
            <span className={`badge ${statusColors[rowData.status] || 'bg-secondary'}`}>
                {rowData.status}
            </span>
        );
    };

    const paymentTypeBodyTemplate = (rowData) => {
        const typeColors = {
            'Prepaid': 'bg-success',
            'Postpaid': 'bg-warning',
            'Token Based': 'bg-primary'
        };

        return (
            <span className={`badge ${typeColors[rowData.payment_type] || 'bg-secondary'}`}>
                {rowData.payment_type}
            </span>
        );
    };

    const currentBalanceBodyTemplate = (rowData) => {
        if (balanceLoading && !rowData.balanceLoaded) {
            return (
                <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-1" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <small>Loading...</small>
                </div>
            );
        }

        const balance = rowData.currentBalance || 0;
        const isNegative = balance < 0;

        return (
            <span className={isNegative ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                KES {balance.toFixed(2)}
            </span>
        );
    };

    const negativeBalanceBodyTemplate = (rowData) => {
        if (balanceLoading && !rowData.balanceLoaded) {
            return (
                <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-1" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <small>Loading...</small>
                </div>
            );
        }

        const negativeBalance = rowData.negativeBalance || 0;

        return (
            <span className={negativeBalance > 0 ? 'text-danger fw-bold' : 'text-muted'}>
                KES {negativeBalance.toFixed(2)}
            </span>
        );
    };

    const handleViewCustomer = (customer) => {
        const { payment_type, _id } = customer;

        const getNavigationPath = (paymentType, accountId) => {
            const basePath = "/facility/utility_management/meter_management/power_meters/view_power_meter_tabs";

            switch (paymentType) {
                case 'Prepaid':
                    return `${basePath}/view_prepaid_power_customer/${accountId}`;
                case 'Postpaid':
                    return `${basePath}/view_postpaid_power_customer/${accountId}`;
                case 'Token Based':
                    return `${basePath}/view_prepaid_power_customer/${accountId}`;
                default:
                    // Fallback to prepaid 
                    console.warn(`Unknown payment type: ${paymentType}. Defaulting to prepaid view.`);
                    return `${basePath}/view_prepaid_power_customer/${accountId}`;
            }
        };

        const navigationPath = getNavigationPath(payment_type, _id);
        navigate(navigationPath);
    };

    const handleRefreshBalances = async () => {
        if (customers.length > 0) {
            await fetchCustomersBalances(customers);
            toastify('Balances refreshed successfully', 'success');
        }
    };

    // Filter customers based on search term
    const filteredCustomers = customers.filter(customer => {
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();
        return (
            customer.customerName?.toLowerCase().includes(searchLower) ||
            customer.phoneNumber?.toLowerCase().includes(searchLower) ||
            customer.email?.toLowerCase().includes(searchLower) ||
            customer.account_no?.toLowerCase().includes(searchLower) ||
            customer.unitInfo?.unitName?.toLowerCase().includes(searchLower)
        );
    });

    if (!selectedFacilityId) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    <p>Please select a facility first</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="text-center py-5">
                        <i className="ti ti-loader fa-spin fa-2x"></i>
                        <p className="mt-2">Loading customers...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="col-md-3 col-xs-12">
                        <input
                            className="form-control"
                            placeholder="Search customers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div>
                        <Button
                            label="Refresh Balances"
                            icon="ti ti-refresh"
                            className="btn btn-primary btn-sm"
                            onClick={handleRefreshBalances}
                            loading={balanceLoading}
                            disabled={customers.length === 0}
                        />
                    </div>
                </div>

                <div className="dt-responsive table-responsive">
                    <DataTable
                        value={filteredCustomers}
                        emptyMessage="No customers found."
                        sortMode="multiple"
                        paginator
                        rows={rows}
                        rowsPerPageOptions={rowsPerPageOptions}
                        stripedRows
                        tableStyle={{ minWidth: '50rem' }}
                    >
                        <Column field="account_no" header="Account No" sortable />
                        <Column field="customerName" header="Customer Name" sortable />
                        <Column field="phoneNumber" header="Phone" />
                        <Column
                            field="unitInfo.unitName"
                            sortable
                            header="Unit"
                            body={(rowData) => rowData.unitInfo?.unitName || 'N/A'}
                        />
                        <Column field="payment_type" header="Payment Type" body={paymentTypeBodyTemplate} />
                        <Column
                            field="currentBalance"
                            header="Current Balance"
                            body={currentBalanceBodyTemplate}
                            sortable
                        />
                        <Column
                            field="negativeBalance"
                            header="Negative Balance"
                            body={negativeBalanceBodyTemplate}
                            sortable
                        />
                        <Column field="status" header="Status" body={statusBodyTemplate} />
                        <Column
                            header="Actions"
                            body={(rowData) => (
                                <Button
                                    icon="ti ti-eye"
                                    className="p-button-rounded p-button-text"
                                    onClick={() => handleViewCustomer(rowData)}
                                    tooltip="View Customer"
                                />
                            )}
                        />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default CustomersTab;